// frontend/src/components/FAQSection.jsx
import React, { useState } from "react";
import jsPDF from "jspdf";

const faqData = [
  {
    question: "How old do I need to be to start driving lessons?",
    answer:
      "You must be at least 17 years old and hold a valid provisional driving licence.",
  },
  {
    question: "How do I apply for a provisional licence?",
    answer:
      "You can apply online through the DVLA website. Most licences arrive within one week.",
  },
  {
    question: "Do I need to pass the theory test before lessons?",
    answer:
      "No. You can start lessons at any time, but you must pass the theory test before booking the practical test.",
  },
  {
    question: "How many lessons will I need?",
    answer:
      "Most learners need around 40–45 hours of professional tuition plus private practice.",
  },
  {
    question: "Do you offer pick-up and drop-off?",
    answer:
      "Yes, we can pick you up from home, work, college, or another agreed location.",
  },
  {
    question: "What type of car will I learn in?",
    answer:
      "You will learn in a modern, fully insured, dual-controlled manual or automatic vehicle.",
  },
];

const termsData = [
  {
    title: "Your Instructor",
    content:
      "Your Instructor is a self-employed franchisee operating under Drive with Portland. Although your Instructor works in association with Drive with Portland, they are self-employed and fully responsible for delivering your driving tuition.",
  },
  {
    title: "The Role of Drive with Portland",
    items: [
      "Drive with Portland acts as an agent between you and your Instructor.",
      "We endeavour to match you with the most suitable Instructor based on your requirements, availability, and vehicle preference (manual or automatic).",
    ],
  },
  {
    title: "Driving Lessons",
    items: [
      "The contract for driving tuition is solely between you and your Instructor.",
      "You and your Instructor are responsible for agreeing lesson dates, times, pick-up/drop-off locations and lesson duration.",
      "It is the pupil’s responsibility to confirm whether manual or automatic tuition is required before lessons begin.",
    ],
  },
  {
    title: "Payments & Booking",
    items: [
      "Payment arrangements must be agreed before lessons commence.",
      "Payment options include cash or bank transfer directly to your Instructor, or bank/card payment to Drive with Portland.",
      "Drive with Portland accepts no liability for payments made directly to your Instructor.",
      "Receipts should be requested for cash payments.",
    ],
  },
  {
    title: "Block Bookings & Validity",
    items: [
      "Prepaid block bookings are charged at the lesson rate in place at the time of purchase.",
      "Block bookings are valid for 3 months from the date of payment.",
      "All prepaid lessons must be taken within this period unless agreed otherwise in writing.",
      "No refunds will be issued for unused lessons after 3 months.",
      "Pay-as-you-go lesson prices may be subject to change with prior notice.",
    ],
  },
  {
    title: "Cancellations & Rescheduling",
    items: [
      "A minimum of 48 hours’ notice is required to cancel or reschedule a lesson.",
      "If less than 48 hours’ notice is provided, the full lesson fee will be charged.",
      "All cancellations must be arranged directly with your Instructor.",
    ],
  },
  {
    title: "Transfer of Lessons",
    items: [
      "Lessons purchased in your name are non-transferable and may not be sold or transferred to another person without written consent from both Drive with Portland and your Instructor.",
    ],
  },
  {
    title: "Late Arrival Policy",
    items: [
      "If you arrive late for your lesson, the lesson will still finish at the scheduled time and the full lesson fee will apply.",
      "If your Instructor is late, the lost time will either be added to the lesson or rearranged at no extra cost.",
    ],
  },
  {
    title: "Driving Test & Car Hire Policy",
    items: [
      "Driving test bookings are the pupil’s responsibility unless otherwise agreed.",
      "Car hire for driving tests must be agreed in advance.",
      "If you fail to attend your test or it is cancelled without sufficient notice, the agreed test car fee may still apply.",
      "Your Instructor reserves the right to refuse use of the vehicle for test if they believe you are not at test standard or it would be unsafe.",
    ],
  },
  {
    title: "Refunds",
    content:
      "Any agreed refunds will be processed within 14 working days. Refunds will be returned using the original payment method where possible.",
  },
];

const FAQSection = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const generateAndDownloadPDF = () => {
    setIsGenerating(true);

    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = margin;

      // Header gradient
      doc.setFillColor(29, 78, 216);
      doc.rect(0, 0, pageWidth, 40, "F");

      // Title
      doc.setFontSize(28);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.text("Drive With Portland", pageWidth / 2, 25, { align: "center" });

      // Subtitle
      doc.setFontSize(22);
      doc.setTextColor(29, 78, 216);
      doc.text("Pupil Terms & Conditions", pageWidth / 2, 55, { align: "center" });

      // Date
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      const currentDate = new Date().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      doc.text(`Effective Date: ${currentDate}`, pageWidth / 2, 65, { align: "center" });

      // Line
      doc.setDrawColor(29, 78, 216);
      doc.setLineWidth(0.5);
      doc.line(margin, 70, pageWidth - margin, 70);

      yPos = 80;

      // Add terms content (same logic as before – titles bold, bullets indented)
      doc.setFontSize(11);
      doc.setTextColor(50, 50, 50);

      termsData.forEach((section, index) => {
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = margin;
        }

        // Section title
        doc.setFontSize(14);
        doc.setTextColor(29, 78, 216);
        doc.setFont("helvetica", "bold");
        doc.text(`${index + 1}. ${section.title.toUpperCase()}`, margin, yPos);
        yPos += 10;

        // Content or items
        doc.setFontSize(11);
        doc.setTextColor(50, 50, 50);
        doc.setFont("helvetica", "normal");

        if (section.content) {
          const contentLines = doc.splitTextToSize(
            section.content,
            pageWidth - 2 * margin
          );
          doc.text(contentLines, margin + 5, yPos);
          yPos += contentLines.length * 6 + 5;
        }

        if (section.items) {
          section.items.forEach((item) => {
            if (yPos > pageHeight - 40) {
              doc.addPage();
              yPos = margin;
            }
            const bulletPoint = `• ${item}`;
            const itemLines = doc.splitTextToSize(
              bulletPoint,
              pageWidth - 2 * margin - 10
            );
            doc.text(itemLines, margin + 10, yPos);
            yPos += itemLines.length * 6 + 4;
          });
        }

        yPos += 10;
      });

      // Footer on all pages
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: "center" });

        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

        doc.setFontSize(8);
        doc.text(
          `© ${new Date().getFullYear()} Drive With Portland. All rights reserved.`,
          pageWidth / 2,
          pageHeight - 5,
          { align: "center" }
        );
      }

      doc.save("Drive-with-Portland-Pupil-Terms-and-Conditions.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("There was an error generating the PDF. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <section className="w-full bg-gradient-to-b from-gray-50 to-gray-100 py-24 px-4 sm:px-6 lg:px-16 font-inter">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#1D4ED8]">
            Frequently Asked Questions
          </h2>
          <p className="mt-5 text-gray-600 text-lg max-w-3xl mx-auto">
            Clear answers to help you start your driving journey with confidence.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          {/* LEFT SIDE – FAQ */}
          <div className="space-y-5">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className="faq-item bg-white border border-gray-200 pb-4 rounded-3xl shadow-2xl overflow-hidden transition hover:shadow-3xl"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="faq-header w-full flex items-center gap-4 px-6 py-4 text-left group"
                >
                  <span className="faq-arrow transition-transform duration-500 text-[#1D4ED8] text-xl group-hover:text-blue-600">
                    {openFaq === index ? "➜" : "➜"}
                  </span>
                  <span className="text-lg md:text-xl font-semibold text-gray-800 group-hover:text-blue-700">
                    {faq.question}
                  </span>
                </button>
                <div
                  className={`faq-content transition-all duration-500 ease-in-out ${
                    openFaq === index
                      ? "max-h-[500px] px-6 pb-12 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <p className="text-gray-600 text-base">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT SIDE – TERMS */}
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 p-10 max-h-[650px] overflow-y-auto relative">
            {/* Decorative shapes */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-bl-full opacity-40 animate-pulse"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-50 rounded-tr-full opacity-30 animate-pulse"></div>

            <h3 className="text-3xl font-extrabold text-[#1D4ED8] mb-8">
              Pupils Terms & Conditions
            </h3>

            <div className="space-y-7 text-gray-600 text-sm">
              {termsData.map((section, index) => (
                <div key={index}>
                  <h4 className="font-semibold text-blue-700 mb-2">
                    {section.title}
                  </h4>
                  {section.content && <p>{section.content}</p>}
                  {section.items && (
                    <ul className="list-disc ml-5 space-y-1">
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* Download Options at the end */}
            <div className="mt-10 pt-8 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <button
                    onClick={generateAndDownloadPDF}
                    disabled={isGenerating}
                    className={`${
                      isGenerating
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-[#1D4ED8] hover:bg-blue-700"
                    } text-white font-semibold py-2 px-6 rounded-lg shadow-md transition duration-300 ease-in-out hover:shadow-lg flex items-center justify-center gap-2`}
                  >
                    {isGenerating ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Generating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-file-pdf"></i>
                        Download PDF
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;